<!-- Modal Backdrop -->
<div class="screen-recorder-backdrop" 
     *ngIf="isModalVisible && !isMinimized"
    [class.dark-mode]="theme.isDark"
     (click)="onBackdropClick($event)">
  
  <!-- Modal Container -->
  <div class="screen-recorder-modal" [class.dark-mode]="theme.isDark">
    
    <!-- Header -->
    <div class="modal-header">
      <div class="modal-title">
        <i class="fa-solid fa-video"></i>
        <span>Screen Recorder</span>
      </div>
      <div class="modal-header-actions">
        <!-- Show minimize during active recording states -->
        <button class="header-btn minimize-btn" 
                *ngIf="state === 'recording' || state === 'countdown' || state === 'paused'"
                (click)="minimize()"
                title="Minimize">
          <i class="fa-solid fa-minus"></i>
        </button>
        <!-- Show close button when not actively recording -->
        <button class="header-btn close-btn" 
                *ngIf="state !== 'recording' && state !== 'countdown' && state !== 'paused'"
                (click)="close()"
                title="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="modal-body">
      
      <!-- Browser Not Supported -->
      <div class="unsupported-message" *ngIf="capabilities && !capabilities.isFullySupported">
        <div class="unsupported-icon">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <h3>Browser Not Supported</h3>
        <p>{{ capabilities.message }}</p>
        <p class="browser-suggestion">
          Please use <strong>Google Chrome</strong>, <strong>Microsoft Edge</strong>, or <strong>Firefox</strong> to record your screen.
        </p>
      </div>

      <!-- Setup View (Idle State) -->
      <div class="setup-view" *ngIf="capabilities?.isFullySupported && state === 'idle'">
        <div class="native-permission-card native-recorder-panel" *ngIf="showNativeRecorderPanel" [class.needs-attention]="needsNativePermissionAttention">
          <div class="native-permission-header">
            <div>
              <h3>{{ nativeCapturePermissions?.title || 'Platfio Native Capture' }}</h3>
              <p>{{ nativeCapturePermissions?.message }}</p>
              <p *ngIf="nativeCapturePermissions?.appLabel">
                Look for <strong>{{ nativeCapturePermissions?.appLabel }}</strong> in your system privacy list.
              </p>
            </div>
            <div class="native-permission-status" *ngIf="!isLoadingNativePermissions">
              {{ nativeCapturePermissions?.screenStatus }}
            </div>
          </div>

          <div class="native-permission-loading" *ngIf="isLoadingNativePermissions">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Checking native capture permissions...</span>
          </div>

          <ol class="native-permission-steps" *ngIf="!isLoadingNativePermissions && nativeCapturePermissions?.steps?.length">
            <li *ngFor="let step of nativeCapturePermissions?.steps">{{ step }}</li>
          </ol>

          <div class="native-permission-actions" *ngIf="!isLoadingNativePermissions">
            <button
              class="permission-btn"
              *ngIf="shouldShowNativeAccessRequest"
              (click)="requestNativeCaptureAccess()">
              {{ nativeCapturePermissions?.requestAccessButtonLabel || 'Request Screen Access' }}
            </button>
            <button
              class="permission-btn"
              *ngIf="nativeCapturePermissions?.settingsAvailable"
              (click)="openNativeCaptureSettings('screen')">
              {{ nativeCapturePermissions?.settingsButtonLabel || 'Open Capture Settings' }}
            </button>
            <button class="permission-btn secondary" (click)="refreshNativeCapturePermissions()">
              Refresh Status
            </button>
          </div>

          <div class="native-source-section">
            <div class="native-source-heading">
              <div>
                <h4>Choose Display</h4>
                <p>Select the screen Platfio should record. The selected display is used for both recording and screenshots.</p>
              </div>
              <button class="permission-btn secondary" (click)="refreshNativeCaptureSources()">
                Refresh Displays
              </button>
            </div>

            <div class="native-source-loading" *ngIf="isLoadingNativeSources">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Loading displays...</span>
            </div>

            <div class="native-source-grid" *ngIf="!isLoadingNativeSources && nativeCaptureSources.length > 0">
              <button
                class="native-source-card"
                *ngFor="let source of nativeCaptureSources"
                [class.selected]="source.id === selectedNativeSourceId"
                (click)="selectNativeSource(source.id)">
                <div class="native-source-thumbnail" [class.empty]="!source.thumbnailDataUrl">
                  <img *ngIf="source.thumbnailDataUrl" [src]="source.thumbnailDataUrl" [alt]="source.name">
                  <div class="native-source-placeholder" *ngIf="!source.thumbnailDataUrl">
                    <i class="fa-solid fa-display"></i>
                  </div>
                </div>
                <div class="native-source-meta">
                  <span class="native-source-name">{{ source.name }}</span>
                  <span class="native-source-display-id" *ngIf="source.displayId">Display {{ source.displayId }}</span>
                </div>
              </button>
            </div>

            <div class="native-source-empty" *ngIf="!isLoadingNativeSources && nativeCaptureSources.length === 0">
              <i class="fa-solid fa-display"></i>
              <span>No displays were found for Platfio capture.</span>
            </div>
          </div>
        </div>
        
        <!-- Live Preview Area -->
        <div class="preview-area" [class.has-preview]="isScreenActive || isWebcamActive || !!selectedNativeSource?.thumbnailDataUrl">
          <!-- Canvas for composite preview -->
          <canvas #previewCanvas class="preview-canvas" [class.active]="(isScreenActive && !isElectronNativeCapture) || isWebcamActive"></canvas>

          <img
            *ngIf="isElectronNativeCapture && !isWebcamActive && selectedNativeSource?.thumbnailDataUrl"
            class="native-preview-image"
            [src]="selectedNativeSource?.thumbnailDataUrl"
            [alt]="selectedNativeSource?.name || 'Selected display'">
          
          <!-- Placeholder when no screen capture and no webcam -->
          <div class="preview-placeholder" *ngIf="!isScreenActive && !isWebcamActive && !(isElectronNativeCapture && selectedNativeSource?.thumbnailDataUrl)">
            <i class="fa-solid fa-desktop"></i>
            <span>{{ isElectronNativeCapture ? 'Choose which display Platfio should capture' : 'Click "Share Screen" to start' }}</span>
          </div>
          
          <!-- Loading spinner -->
          <div class="preview-loading" *ngIf="isLoadingScreen">
            <i class="fa-solid fa-spinner fa-spin"></i>
          </div>
        </div>

        <!-- Source Controls -->
        <div class="source-controls">
          <button class="source-btn"
                  [class.active]="isScreenActive"
                  [class.loading]="isLoadingScreen || (isElectronNativeCapture && (isLoadingNativePermissions || isLoadingNativeSources))"
                  [disabled]="isElectronNativeCapture && (isLoadingNativePermissions || isLoadingNativeSources)"
                  (click)="toggleScreen()">
            <span class="source-icon">
              <i class="fa-solid fa-spinner fa-spin" *ngIf="isLoadingScreen || (isElectronNativeCapture && (isLoadingNativePermissions || isLoadingNativeSources))"></i>
              <i class="fa-solid fa-desktop" *ngIf="!(isLoadingScreen || (isElectronNativeCapture && (isLoadingNativePermissions || isLoadingNativeSources)))"></i>
            </span>
            <span class="source-label">
              {{ isElectronNativeCapture ? (isScreenActive ? 'Screen Ready' : 'Preparing Screen') : (isScreenActive ? 'Screen Active' : 'Share Screen') }}
            </span>
            <span class="source-indicator" *ngIf="isScreenActive">
              <i class="fa-solid fa-check"></i>
            </span>
          </button>
          
          <!-- Microphone Button -->
          <button class="source-btn" 
                  [class.active]="isAudioActive"
                  [class.loading]="isLoadingAudio"
                  [disabled]="!capabilities?.audio"
                  (click)="toggleAudio()">
            <span class="source-icon">
              <i class="fa-solid fa-spinner fa-spin" *ngIf="isLoadingAudio"></i>
              <i class="fa-solid fa-microphone" *ngIf="!isLoadingAudio"></i>
            </span>
            <span class="source-label">
              {{ isAudioActive ? 'Mic Active' : 'Add Microphone' }}
            </span>
            <span class="source-indicator" *ngIf="isAudioActive">
              <i class="fa-solid fa-check"></i>
            </span>
          </button>

            <!-- Webcam Button -->
          <button class="source-btn" 
                  [class.active]="isWebcamActive"
                  [class.loading]="isLoadingWebcam"
              [disabled]="!capabilities?.webcam || !canUseWebcamInCurrentMode"
                  (click)="toggleWebcam()">
            <span class="source-icon">
              <i class="fa-solid fa-spinner fa-spin" *ngIf="isLoadingWebcam"></i>
              <i class="fa-solid fa-video" *ngIf="!isLoadingWebcam"></i>
            </span>
            <span class="source-label">
              {{ isElectronNativeCapture ? (isWebcamActive ? 'Webcam' : 'Add Webcam') : (isWebcamActive ? 'Webcam Active' : 'Add Webcam') }}
            </span>
            <span class="source-indicator" *ngIf="isWebcamActive">
              <i class="fa-solid fa-check"></i>
            </span>
          </button>
        </div>

        <!-- Compact Settings -->
        <div class="compact-settings" *ngIf="isWebcamActive || isAudioActive || isScreenActive || isElectronNativeCapture || userId">
          <!-- Webcam settings row -->
          <div class="settings-inline" *ngIf="isWebcamActive">
            <div class="inline-setting">
              <span>Position</span>
              <select [(ngModel)]="webcamPosition" (ngModelChange)="onPositionChange($event)">
                <option value="bottom-right">Bottom Right</option>
                <option value="bottom-left">Bottom Left</option>
                <option value="top-right">Top Right</option>
                <option value="top-left">Top Left</option>
              </select>
            </div>
            <div class="inline-setting">
              <span>Size</span>
              <input type="range" [(ngModel)]="webcamSize" (ngModelChange)="onSizeChange($event)" min="80" max="300" step="10">
              <span class="range-value">{{ webcamSize }}</span>
            </div>
            <div class="inline-setting">
              <span>Border</span>
              <input type="range" [(ngModel)]="webcamBorderWidth" (ngModelChange)="onBorderWidthChange($event)" min="0" max="10" step="1">
              <span class="range-value">{{ webcamBorderWidth }}</span>
            </div>
          </div>

          <!-- Mic & Countdown row -->
          <div class="settings-inline">
            <div class="inline-setting" *ngIf="isAudioActive && audioDevices.length > 0">
              <span>Mic</span>
              <select [(ngModel)]="selectedAudioDeviceId" (ngModelChange)="onAudioDeviceChange($event)">
                <option *ngFor="let device of audioDevices" [value]="device.deviceId">{{ getAudioDeviceLabel(device) }}</option>
              </select>
            </div>
            <div class="inline-setting" *ngIf="isElectronNativeCapture && nativeCaptureSources.length > 0">
              <span>Display</span>
              <select [ngModel]="selectedNativeSourceId" (ngModelChange)="selectNativeSource($event)">
                <option *ngFor="let source of nativeCaptureSources" [ngValue]="source.id">{{ source.name }}</option>
              </select>
            </div>
            <div class="inline-setting" *ngIf="isScreenActive">
              <span>Countdown</span>
              <select [(ngModel)]="countdownSeconds" (ngModelChange)="onCountdownChange($event)">
                <option [ngValue]="0">None</option>
                <option [ngValue]="3">3s</option>
                <option [ngValue]="5">5s</option>
                <option [ngValue]="10">10s</option>
              </select>
            </div>
            <div class="inline-setting history-setting" *ngIf="userId">
              <button
                #historyTrigger
                type="button"
                class="history-trigger"
                [class.active]="isHistoryOpen"
                [disabled]="isLoadingHistory"
                (click)="toggleRecordingHistory($event)"
                aria-haspopup="dialog"
                [attr.aria-expanded]="isHistoryOpen"
                title="Recording history">
                <i class="fa-solid fa-spinner fa-spin" *ngIf="isLoadingHistory"></i>
                <i class="fa-solid fa-clock-rotate-left" *ngIf="!isLoadingHistory"></i>
                <span>History</span>
                <span class="history-trigger-count" *ngIf="recordingHistory.length > 0">{{ recordingHistory.length }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div class="error-message" *ngIf="error">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>{{ error }}</span>
        </div>

      </div>

      <!-- Countdown View -->
      <div class="countdown-view" *ngIf="state === 'countdown'">
        <div class="countdown-number">{{ countdown }}</div>
        <p>Get ready...</p>
        <button class="btn-cancel" (click)="cancelRecording()">
          Cancel
        </button>
      </div>

      <!-- Recording View -->
      <div class="recording-view" *ngIf="state === 'recording'">
        <div class="recording-indicator">
          <span class="recording-dot" [class.warning]="hasWarning"></span>
          <span class="recording-label">REC</span>
        </div>
        <div class="recording-time">{{ formatTime(recordingTime) }}</div>
        
        <!-- Warning banner -->
        <div class="warning-banner" *ngIf="hasWarning">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>{{ warningMessage }}</span>
        </div>
        
        <p class="recording-hint" *ngIf="!hasWarning">Click "Stop" when you're done recording</p>
        <div class="recording-actions">
          <button class="btn-stop" (click)="stopRecording()">
            <i class="fa-solid fa-stop"></i>
            <span>Stop Recording</span>
          </button>
          <button class="btn-discard" (click)="discardRecording()">
            <i class="fa-solid fa-trash"></i>
            <span>Discard</span>
          </button>
        </div>
      </div>

      <!-- Processing View -->
      <div class="processing-view" *ngIf="state === 'processing'">
        <div class="processing-spinner">
          <i class="fa-solid fa-spinner fa-spin"></i>
        </div>
        <p>Processing your recording...</p>
      </div>

      <!-- Complete View -->
      <div class="complete-view" *ngIf="state === 'complete'">
        <div class="success-icon">
          <i class="fa-solid fa-check-circle"></i>
        </div>
        <h3>Recording Saved!</h3>
        
        <!-- Auto-copied notification -->
        <div class="auto-copied-notification" *ngIf="showAutoCopiedNotification">
          <i class="fa-solid fa-clipboard-check"></i>
          <span>Link copied to clipboard</span>
        </div>
        
        <!-- Link Mode Segment Control -->
        <div class="segment-control">
          <button 
            class="segment-btn" 
            [class.active]="linkMode === 'share'" 
            (click)="linkMode = 'share'">
            Share Link
          </button>
          <button 
            class="segment-btn" 
            [class.active]="linkMode === 'embed'" 
            (click)="linkMode = 'embed'">
            Embed
          </button>
        </div>

        <!-- Share Link Mode -->
        <div class="link-mode-content" *ngIf="linkMode === 'share'">
          <!-- Media Metadata Editing -->
          <div class="metadata-section">
            <div class="metadata-field">
              <label>Title</label>
              <input type="text" 
                     [(ngModel)]="mediaTitle" 
                     placeholder="Enter a title..."
                     (blur)="saveMediaMetadata()"
                     (keyup.enter)="saveMediaMetadata()">
            </div>
          </div>
          
          <!-- Share Page URL -->
          <div class="url-section" *ngIf="sharePageUrl">
            <label class="url-label">Share Link</label>
            <div class="url-container">
              <input type="text" 
                     [value]="sharePageUrl" 
                     readonly 
                     class="url-input">
              <button class="btn-copy" [class.copied]="copiedShare" (click)="copySharePageUrl()" title="Copy share link">
                <i class="fa-solid" [class.fa-copy]="!copiedShare" [class.fa-check]="copiedShare"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Embed Mode -->
        <div class="link-mode-content" *ngIf="linkMode === 'embed'">
          <!-- Upload in progress -->
          <div class="embed-upload-progress" *ngIf="!downloadUrl">
            <div class="upload-progress">
              <div class="progress-bar">
                <div class="progress-fill" [style.width.%]="uploadProgress"></div>
              </div>
              <span class="progress-text">{{ uploadProgress }}%</span>
            </div>
            <p class="upload-hint">Uploading your recording...</p>
          </div>

          <!-- Direct URL (only when upload complete) -->
          <div class="url-section" *ngIf="downloadUrl">
            <label class="url-label">Direct URL</label>
            <div class="url-container">
              <input type="text" 
                     [value]="downloadUrl" 
                     readonly 
                     class="url-input">
              <button class="btn-copy" [class.copied]="copiedDirect" (click)="copyDirectUrl()" title="Copy direct URL">
                <i class="fa-solid" [class.fa-copy]="!copiedDirect" [class.fa-check]="copiedDirect"></i>
              </button>
            </div>
          </div>

          <!-- Iframe Embed Code (only when upload complete) -->
          <div class="url-section" *ngIf="downloadUrl">
            <label class="url-label">Embed Code</label>
            <div class="url-container">
              <input type="text" 
                     [value]="embedCode" 
                     readonly 
                     class="url-input embed-code">
              <button class="btn-copy" [class.copied]="copiedEmbed" (click)="copyEmbedCode()" title="Copy embed code">
                <i class="fa-solid" [class.fa-copy]="!copiedEmbed" [class.fa-check]="copiedEmbed"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="complete-actions">
          <button class="btn-new" (click)="startNewRecording()">
            <i class="fa-solid fa-plus"></i>
            <span>New Recording</span>
          </button>
        </div>
      </div>

      <!-- Error View -->
      <div class="error-view" *ngIf="state === 'error'">
        <div class="error-icon">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <h3>Something went wrong</h3>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button class="btn-retry" (click)="startNewRecording()">
            <i class="fa-solid fa-rotate-right"></i>
            <span>Try Again</span>
          </button>
          <button class="btn-close" (click)="close()">
            <i class="fa-solid fa-xmark"></i>
            <span>Close</span>
          </button>
        </div>
      </div>
    </div>

    <div class="modal-footer" *ngIf="capabilities?.isFullySupported && state === 'idle'">
      <button #startRecordingBtn
              class="btn-record"
              [disabled]="!canStartRecording"
              (click)="startRecording()">
        <i class="fa-solid fa-circle"></i>
        <span>Start Recording</span>
      </button>
    </div>
  </div>
</div>

<ng-template #historyPopoverTemplate>
  <div class="history-popover">
    <div class="history-state" *ngIf="isLoadingHistory">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span>Loading recordings...</span>
    </div>

    <div class="history-state error" *ngIf="!isLoadingHistory && historyError">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <span>{{ historyError }}</span>
    </div>

    <div class="history-state empty" *ngIf="!isLoadingHistory && !historyError && recordingHistory.length === 0">
      <i class="fa-solid fa-video-slash"></i>
      <span>No saved videos yet.</span>
    </div>

    <div class="history-list" *ngIf="!isLoadingHistory && !historyError && recordingHistory.length > 0">
      <button
        type="button"
        class="history-item"
        *ngFor="let recording of recordingHistory"
        [class.copied]="copiedHistoryShareId === recording.id"
        (click)="copyHistoryShareUrl(recording, $event)">
        <span class="history-item-icon">
          <i class="fa-solid" [class.fa-video]="copiedHistoryShareId !== recording.id" [class.fa-check]="copiedHistoryShareId === recording.id"></i>
        </span>
        <span class="history-item-main">
          <span class="history-item-title">{{ recording.displayName }}</span>
          <span class="history-item-meta">
            {{ formatHistoryDate(recording) }} &middot; {{ formatHistoryDuration(recording) }}
            <ng-container *ngIf="formatHistorySize(recording) as size"> &middot; {{ size }}</ng-container>
          </span>
        </span>
        <span class="history-item-status" [class.copied]="copiedHistoryShareId === recording.id">
          {{ copiedHistoryShareId === recording.id ? 'Copied' : 'Copy link' }}
        </span>
      </button>
    </div>
  </div>
</ng-template>
